/**
 * https://medium.com/clever-franke/create-a-react-slidetoggle-component-with-hooks-and-react-spring-748919c38667
 */
import React, { useRef, MutableRefObject } from 'react'
import styles from '~/styles/utils/animation/toggle.module.scss'
import { useTransition, animated } from 'react-spring'
import { ChildProps } from '~/common/define/props'

const visibleStyle = { height: 'auto', opacity: 1, overflow: "visible" }
const hiddenStyle = { opacity: 0, height: 0, overflow: "hidden" }

function getElementHeight(ref: MutableRefObject<HTMLInputElement>) {
  return ref.current ? ref.current.getBoundingClientRect().height : 0
}

export interface Props extends ChildProps {
  show: boolean
  forceSlideIn: boolean
}

/** The children of this component will slide down on mount and will slide up on unmount */
const SlideToggle = ({show, children, forceSlideIn = false}: Props) => {
  const isVisibleOnMount = useRef(show && !forceSlideIn)
  const containerRef = useRef(null)
  const innerRef = useRef(null)

  const enter = () => async (next:(items: any) => void, cancel:(() => void)) => {
    const height = getElementHeight(innerRef)
    cancel && cancel()

    if (height) {
      next && await next({height, opacity: 1, overflow: "hidden"})
      next && await next(visibleStyle)
    }
  }

  const leave = () => async (next:(items: any) => void, cancel:(() => void)) => {
    const height = getElementHeight(containerRef)
    cancel && cancel()

    if (height) {
      next && await next({height, overflow: "hidden"})
      next && await next(hiddenStyle)
    }
  }

  const transitions = useTransition(show, null, {
    enter: enter as any,
    leave: leave as any,
    from: isVisibleOnMount.current ? visibleStyle : hiddenStyle,
    unique: true
  })

  return (
    <>
      {transitions.map(({item: showing, props, key}) => {
        if (showing) {
          return (
            <animated.div ref={containerRef} key={key} style={props}>
              <div className={styles.slide} ref={innerRef}>{children}</div>
            </animated.div>
          )
        }
        // for dynamic loading components
        return (
          <div key={key} style={{display: 'none' }}>
            {children}
          </div>
        )
      })}
    </>
  )
}

export default SlideToggle